import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePoints } from '../contexts/PointsContext';
import { useTheme } from '../contexts/ThemeContext';

const FiHome = FiIcons.FiHome;
const FiList = FiIcons.FiList;
const FiPlus = FiIcons.FiPlus;
const FiSearch = FiIcons.FiSearch;
const FiCheckCircle = FiIcons.FiCheckCircle;
const FiClock = FiIcons.FiClock;
const FiAlertCircle = FiIcons.FiAlertCircle;
const FiFolders = FiIcons.FiFolder;
const FiBarChart2 = FiIcons.FiBarChart2;
const FiCalendar = FiIcons.FiCalendar;
const FiTimeboxing = FiIcons.FiClock;
const FiSettings = FiIcons.FiSettings;
const FiSun = FiIcons.FiSun;
const FiMoon = FiIcons.FiMoon;
const FiAward = FiIcons.FiAward;
const FiInfo = FiIcons.FiInfo;
const FiTag = FiIcons.FiTag;
const FiInbox = FiIcons.FiInbox;
const FiArchive = FiIcons.FiArchive;
const FiTrash2 = FiIcons.FiTrash2;
const FiX = FiIcons.FiX;
const FiChevronDown = FiIcons.FiChevronDown;


const Sidebar = ({
  filter,
  setFilter,
  onAddTask,
  onAddProject,
  searchQuery,
  setSearchQuery,
  projects,
  currentProjectId,
  setCurrentProjectId,
  tasksCount,
  tags,
  selectedTags,
  setSelectedTags,
  onDeleteTag,
  mobileOpen,
  onMobileClose
}) => {
  const location = useLocation();
  const { points, pointsBreakdown } = usePoints();
  const { darkMode, toggleDarkMode } = useTheme();
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = React.useState(true);
  const [popularTagsOpen, setPopularTagsOpen] = React.useState(true);

  const menuItems = [
    {
      path: '/',
      icon: FiHome,
      label: t('navigation.dashboard'),
      isActive: () => location.pathname === '/'
    },
    {
      path: '/tasks',
      icon: FiInbox,
      label: t('navigation.inbox'),
      onClick: () => setCurrentProjectId('default'),
      isActive: () => location.pathname === '/tasks' && currentProjectId === 'default'
    },
    {
      path: '/tasks',
      icon: FiList,
      label: t('navigation.tasks'),
      onClick: () => setCurrentProjectId(null),
      isActive: () => location.pathname === '/tasks' && currentProjectId === null
    },
    {
      path: '/projects',
      icon: FiFolders,
      label: t('navigation.projects'),
      isActive: () => location.pathname === '/projects'
    },
    {
      path: '/gantt',
      icon: FiBarChart2,
      label: t('navigation.gantt'),
      isActive: () => location.pathname === '/gantt'
    },
    {
      path: '/calendar',
      icon: FiCalendar,
      label: t('navigation.calendar'),
      isActive: () => location.pathname === '/calendar'
    },
    {
      path: '/timeboxing',
      icon: FiTimeboxing,
      label: t('navigation.timeboxing'),
      isActive: () => location.pathname === '/timeboxing'
    },
    {
      path: '/archives',
      icon: FiArchive,
      label: t('navigation.archives'),
      isActive: () => location.pathname === '/archives'
    },
    {
      path: '/settings',
      icon: FiSettings,
      label: t('navigation.settings'),
      isActive: () => location.pathname === '/settings'
    }
  ];

  const filterItems = [
    { key: 'all', label: t('navigation.allTasks'), count: tasksCount.total, icon: FiList },
    { key: 'pending', label: t('navigation.pending'), count: tasksCount.pending, icon: FiClock, isPending: true },
    { key: 'completed', label: t('navigation.completed'), count: tasksCount.completed, icon: FiCheckCircle },
    { key: 'high', label: t('navigation.highPriority'), icon: FiAlertCircle },
    { key: 'medium', label: t('navigation.mediumPriority'), icon: FiAlertCircle },
    { key: 'low', label: t('navigation.lowPriority'), icon: FiAlertCircle }
  ];

  const getCurrentProjectName = () => {
    if (!currentProjectId) return t('navigation.allProjects');
    const project = projects.find(p => p.id === currentProjectId);
    return project ? project.name : t('navigation.allProjects');
  };

  // Show only frequently used tags (up to 5)
  const popularTags = tags.slice(0, 5);

  const handleTagChange = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleDeleteTag = (event, tag) => {
    event.preventDefault();
    event.stopPropagation();

    if (window.confirm(t('navigation.confirmDeleteTag', { tagName: tag.name }))) {
      onDeleteTag?.(tag.id);
    }
  };

  const sidebarContent = (
    <div className="p-5">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-[2.5rem] font-bold text-slate-800 dark:text-white">Efficacitas</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleDarkMode}
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg transition-colors hover:scale-110 active:scale-90 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={darkMode ? t('theme.lightMode') : t('theme.darkMode')}
            type="button"
          >
            <SafeIcon icon={darkMode ? FiSun : FiMoon} className="text-lg" />
          </button>
          {mobileOpen && (
            <button
              onClick={onMobileClose}
              className="md:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={t('common.close')}
              type="button"
            >
              <SafeIcon icon={FiX} className="text-lg" />
            </button>
          )}
        </div>
      </div>

        {/* Points Display */}
        <div className="flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg mb-6">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiAward} className="text-yellow-500" />
            <span className="text-slate-700 dark:text-slate-300 font-medium">{t('navigation.points')}</span>
            <SafeIcon
              icon={FiInfo}
              className="text-slate-400 dark:text-slate-500 cursor-help"
              title={t('navigation.pointsTooltip', {
                timeUnits: pointsBreakdown.timeUnits,
                points
              })}
            />
          </div>
          <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{points}</span>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <SafeIcon
            icon={FiSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            placeholder={t('navigation.searchTasks')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
          />
        </div>

        {/* Project Selector */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('navigation.currentProject')}
            </h2>
            <motion.button
              onClick={onAddProject}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
            </motion.button>
          </div>
          <div className="relative">
            <select
              value={currentProjectId || ""}
              onChange={(e) => setCurrentProjectId(e.target.value || null)}
              className="w-full pl-4 pr-10 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-slate-800 dark:text-white"
            >
              <option value="">{t('navigation.allProjects')}</option>
              <option value="default">{t('navigation.inbox')}</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <SafeIcon icon={FiFolders} className="text-slate-400" />
            </div>
          </div>
        </div>

        {/* Add Task Button */}
      <button
        onClick={() => {
          onAddTask();
          onMobileClose?.();
        }}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium mb-8 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
        type="button"
      >
        <SafeIcon icon={FiPlus} className="text-lg" />
        {t('tasks.addTask')}
      </button>

        {/* Navigation */}
        <nav className="mb-8">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">
            {t('navigation.dashboard')}
          </h2>
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.path}
                  onClick={item.onClick}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    item.isActive()
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="text-lg" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Filters */}
        {location.pathname === '/tasks' && (
          <div>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="flex items-center justify-between w-full mb-2 text-left"
              type="button"
            >
              <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {t('common.filter')}
              </h2>
              <motion.div
                animate={{ rotate: filtersOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiChevronDown} className="text-slate-400" />
              </motion.div>
            </button>
            <AnimatePresence initial={false}>
              {filtersOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
                      {getCurrentProjectName()}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {filterItems.map((item) => (
                      <li key={item.key}>
                        <button
                          onClick={() => setFilter(item.key)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            filter === item.key
                              ? item.isPending
                                ? 'bg-amber-50/60 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800/40'
                                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <SafeIcon
                              icon={item.icon}
                              className={`text-lg ${
                                item.key === 'high'
                                  ? 'text-red-500'
                                  : item.key === 'medium'
                                  ? 'text-yellow-500'
                                  : item.key === 'low'
                                  ? 'text-green-500'
                                  : ''
                              }`}
                            />
                            {item.label}
                          </div>
                          {item.count !== undefined && (
                            <span className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-full text-xs font-medium">
                              {item.count}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {tags.length > 0 && (
                    <div className="mt-3">
                      <h3 className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
                        {t('navigation.filterByTags')}
                      </h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {tags.map(tag => (
                          <div key={tag.id} className="flex items-center justify-between gap-2 px-4 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <label className="flex min-w-0 flex-1 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedTags.includes(tag.id)}
                                onChange={() => handleTagChange(tag.id)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="truncate text-slate-600 dark:text-slate-300">{tag.name}</span>
                            </label>
                            {onDeleteTag && (
                              <button
                                type="button"
                                onClick={(event) => handleDeleteTag(event, tag)}
                                className="shrink-0 p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                                title={t('navigation.deleteTag')}
                              >
                                <SafeIcon icon={FiTrash2} className="text-xs" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

      {/* Tags Section */}
      {popularTags.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setPopularTagsOpen(!popularTagsOpen)}
            className="flex items-center justify-between w-full mb-2 text-left"
            type="button"
          >
            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('navigation.popularTags')}
            </h2>
            <motion.div
              animate={{ rotate: popularTagsOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiChevronDown} className="text-slate-400" />
            </motion.div>
          </button>
          <AnimatePresence initial={false}>
            {popularTagsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => setSearchQuery(tag.name)}
                      className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full text-sm flex items-center gap-1 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      type="button"
                    >
                      <SafeIcon icon={FiTag} className="text-xs" />
                      {tag.name}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:block w-80 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl shadow-2xl border-r border-white/20 dark:border-slate-700/40 h-screen overflow-y-auto custom-scrollbar transition-colors duration-300">
        {sidebarContent}
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onMobileClose}
            role="presentation"
          />
          <div className="relative w-80 max-w-[85vw] bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl shadow-2xl h-screen overflow-y-auto transition-colors duration-300 animate-slide-in">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
